import clsx from "clsx";
import { Link } from "gatsby";
import React, { DetailedHTMLProps, FC, HTMLAttributes, useCallback } from "react";

import { disableComparator } from "../../../../features-config.json";
import { useProductsComparator } from "../../../contexts/products-comparator";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { CommonColors } from "../../../types/colors";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Clickable } from "../../buttons/button/clickable";
import { Checkbox } from "../../forms";
import { RichText } from "../../rich-text";
import { ProductCardGallery } from "./product-card-gallery";
import { colorScheme } from "./styles";

export interface ProductCardProps {
  product: Queries.ProductDataFragment;
  setTitleRef?: (el: HTMLDivElement | null) => void;
  titleProps?: Partial<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>;
  color?: CommonColors;
  labels?: Partial<{
    "product-card-see-more": string | null;
    "product-card-compare": string | null;
  }>;
  hideCtas?: boolean;
  isGridFiltered?: boolean;
}

export const ProductCard: FC<ProductCardProps> = ({
  product,
  titleProps: { className: titleClassName, ...otherTitleProps } = {},
  setTitleRef,
  labels,
  color: _color,
  isGridFiltered,
  hideCtas = false,
}) => {
  const pageId = product?.standardpage?.[0]?.id;
  const pageUrl = usePageFullPath(pageId);

  const { category, name, specialization, shortDescription, defaultLocaleEntry } = product;
  const comparatorContext = useProductsComparator();
  const { toggleProduct, isProductSelected, selectedProductsCount, maxProducts } =
    comparatorContext ?? {};

  const color =
    _color ?? (specialization?.machiningType?.material as CommonColors) ?? CommonColors.Light;
  const { categoryClassName } = colorScheme[color];

  const { pushEvent } = useDataLayer();
  const onClickProductSeeMore = useCallback(() => {
    if (defaultLocaleEntry) {
      pushEvent(
        {
          event: GoogleAnalyticsEvent.ClickProduct,
          product_name: defaultLocaleEntry?.name,
          product_id: defaultLocaleEntry?.id,
          product_category: defaultLocaleEntry.specialization?.name ?? null,
          product_category2: defaultLocaleEntry.specialization?.machiningType?.name ?? null,
          product_category3: defaultLocaleEntry.specialization?.machiningType?.material ?? null,
          click_selection: isGridFiltered ? "Product list filtered" : "Product list",
        },
        ["product_id"]
      );
    }
  }, [pushEvent, isGridFiltered, defaultLocaleEntry]);

  const innerComponent = (
    <div
      className={clsx(
        "h-full bg-light rounded-2xl relative",
        /* According to Figma */
        // "outline outline-2 outline-transparent hover:outline-primary outline-offset-0"
        /* According to a common sense of beauty */
        "hover:shadow-lg transition-all duration-300"
      )}
    >
      {category && (
        <div
          className={clsx(
            "absolute top-2 left-2 z-30 rounded-full",
            "py-1 px-3 uppercase text-sm font-bold",
            categoryClassName
          )}
        >
          {category}
        </div>
      )}
      <ProductCardGallery product={product} />
      <div className="p-6 flex-1 flex flex-col gap-3 justify-between">
        <div
          className={clsx("font-bold text-xl", titleClassName)}
          {...otherTitleProps}
          ref={(el) => setTitleRef?.(el)}
        >
          {pageUrl ? <Link to={pageUrl}>{name}</Link> : name}
        </div>
        {shortDescription?.raw && <RichText className="mb-2 flex-1" raw={shortDescription.raw} />}
        {!hideCtas && (
          <div
            className={clsx(
              "flex gap-2 items-end pt-2",
              pageUrl ? "justify-between" : "justify-end"
            )}
          >
            {pageUrl && (
              <Link to={pageUrl}>
                <Clickable variant="primary" size="sm" onClick={onClickProductSeeMore}>
                  {labels?.["product-card-see-more"]}
                </Clickable>
              </Link>
            )}

            {comparatorContext && !disableComparator && (
              <Checkbox
                collapseMessage
                labelPosition="left"
                checked={isProductSelected(product)}
                onChange={() => toggleProduct(product)}
                disabled={!isProductSelected(product) && selectedProductsCount === maxProducts}
              >
                <span className="font-bold pb-[6px]">{labels?.["product-card-compare"]}</span>
              </Checkbox>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return pageUrl && hideCtas ? <a href={pageUrl}>{innerComponent}</a> : innerComponent;
};
